import { supabase, TABLES } from './supabase';
import { Queue } from '../types/queue';
import { Player } from '../types/player';

export class QueueService {
  // 获取当前队列状态
  static async getCurrentQueue(): Promise<Queue | null> {
    const { data, error } = await supabase
      .from(TABLES.QUEUE_STATE)
      .select('*')
      .order('updated_at', { ascending: false })
      .limit(1)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // 没有找到记录，返回null
        return null;
      }
      throw new Error(`获取队列状态失败: ${error.message}`);
    }

    // 获取玩家详细信息
    const playerIds = data.player_order as string[];
    const players = await this.getPlayersByIds(playerIds);

    return {
      id: data.id,
      players,
      currentPosition: data.current_position,
      updatedAt: new Date(data.updated_at),
    };
  }

  // 根据ID数组获取玩家信息
  private static async getPlayersByIds(playerIds: string[]): Promise<Player[]> {
    const { data, error } = await supabase
      .from(TABLES.PLAYERS)
      .select('*')
      .in('id', playerIds);

    if (error) {
      throw new Error(`获取玩家信息失败: ${error.message}`);
    }

    // 按照原始顺序排列玩家
    const playerMap = new Map(data.map(p => [p.id, p]));
    return playerIds
      .map(id => playerMap.get(id))
      .filter(Boolean)
      .map(player => ({
        id: player.id,
        name: player.name,
        number: player.number,
        isActive: player.is_active,
        createdAt: new Date(player.created_at),
      }));
  }

  // 初始化队列状态
  static async initializeQueue(players: Player[]): Promise<Queue> {
    const playerIds = players.map(p => p.id);
    
    const { data, error } = await supabase
      .from(TABLES.QUEUE_STATE)
      .insert({
        player_order: playerIds,
        current_position: 0,
      })
      .select()
      .single();

    if (error) {
      throw new Error(`初始化队列失败: ${error.message}`);
    }

    return {
      id: data.id,
      players,
      currentPosition: data.current_position,
      updatedAt: new Date(data.updated_at),
    };
  }

  // 更新队列状态
  static async updateQueue(queueId: string, players: Player[], currentPosition: number): Promise<Queue> {
    const playerIds = players.map(p => p.id);
    
    const { data, error } = await supabase
      .from(TABLES.QUEUE_STATE)
      .update({
        player_order: playerIds,
        current_position: currentPosition,
        updated_at: new Date().toISOString(),
      })
      .eq('id', queueId)
      .select()
      .single();

    if (error) {
      throw new Error(`更新队列失败: ${error.message}`);
    }

    return {
      id: data.id,
      players,
      currentPosition: data.current_position,
      updatedAt: new Date(data.updated_at),
    };
  }

  // 轮换队列（将第一个人移到队尾）
  static async rotateQueue(currentQueue: Queue): Promise<Queue> {
    if (currentQueue.players.length === 0) {
      return currentQueue;
    }

    const [first, ...rest] = currentQueue.players;
    const rotatedPlayers = [...rest, first];

    return this.updateQueue(currentQueue.id, rotatedPlayers, currentQueue.currentPosition);
  }

  // 获取当前参赛者（前4个人）
  static getCurrentPlayers(queue: Queue): Player[] {
    return queue.players.slice(0, 4);
  }

  // 获取休息的人
  static getRestingPlayer(queue: Queue): Player | null {
    return queue.players.length > 4 ? queue.players[4] : null;
  }
}